import { useEffect } from 'react';

import { useEngineBus } from './useEngineBus.js';

/**
 * @file THE MISSION LIST SUBSCRIPTION, AND THE ASK THAT MAKES IT WORK.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ IT ASKS ON MOUNT, AND THAT IS NOT OPTIONAL
 * ─────────────────────────────────────────────────────────────────────────────
 * `'mission:list'` is published ON CHANGE. Every screen that shows it — the
 * pause menu's list, the phone's missions app — mounts long after the last
 * change, over a loop that the mounting overlay has ALREADY FROZEN. A component
 * that only subscribed would therefore wait for an event that cannot arrive and
 * render an empty list for ever.
 *
 * That is not a hypothetical: `MissionList.jsx` shipped that way the first time
 * out, and its header still carries the sentence. The fix is one line —
 * `bus.emit('mission:request')` — and it works precisely because a bus emit is a
 * FUNCTION CALL rather than a tick: the director republishes synchronously from
 * inside the callback, with the fixed step still at zero.
 *
 * ⚠ THE POINT OF THIS FILE IS THAT THE RULE HAS EXACTLY ONE HOME. The rule is
 * invisible in its consequences (a screen that renders, just empty, with no
 * error anywhere) and it must be re-obeyed by every new consumer. A second
 * screen that hand-rolled the subscription would be a coin flip on whether its
 * author read the other file's header. It cannot be: the phone panel physically
 * cannot mount a mission list without going through here.
 *
 * ⚠ AND THE ORDER IS SAFE. `useEngineBus` installs its subscription in its own
 * effect, and effects run child-first — but both effects belong to the SAME
 * commit, and this one is declared after it in the same function body, so the
 * listener is installed before the ask goes out. Moving the `useEffect` above
 * the `useEngineBus` call would lose the reply.
 *
 * The 60 ms throttle matches `MissionList.jsx`'s original and is far faster than
 * anything this channel does — it fires on state changes, not per frame.
 *
 * @module ui/hooks/useMissionList
 */

/**
 * @param {{bus?: import('../../engine/types.js').EventBus}|null|undefined} engine
 * @returns {any} The last `'mission:list'` payload, or `null` before the first.
 *   Shape is the director's: `{ missions: [...] }`.
 */
export function useMissionList(engine) {
  const list = useEngineBus(engine, 'mission:list', { throttle: 60 });

  // See the header: ask, because nothing is going to tell us.
  useEffect(() => {
    engine?.bus?.emit?.('mission:request');
  }, [engine]);

  return list;
}
